import { skipUnported } from '../unported.js';
import type { MovementTruthCommand } from './client-truth-commit.js';
import type { MovementStatusInputs20 } from '../statuses/application-inputs-20.js';
import { squeezeStateAt, type PlainWalkReaders } from './plain-walk-scope.js';
import { escapeHtml } from '../../shared/cosmetic-card.js';
import { BURROWED_ITEM_ID } from './burrow-move.js';
import { statusItemIdOf, type SqueezeState } from './squeeze-at-rest.js';
import { bodyInSolid } from '../../shared/burrow-route.js';
import { solidContextFromEncounter } from '../../shared/solid-material.js';

type Row = Record<string, unknown>;
export interface MovementDispatchContext {
  encounter: Row;
  read: PlainWalkReaders;
  follower: (id: string) => Row | null;
  name: (row: Row) => string;
}
declare global {
  interface Window {
    ftMovementDispatchContext?: (encounterId: string) => MovementDispatchContext | null;
  }
}

function settlementDuties(row: Row, point: { x: number; y: number; z: number }, context: MovementDispatchContext): string[] {
  const { read, encounter } = context;
  const duties: string[] = [];
  if (read.statuses(row).some(status => statusItemIdOf(status) === BURROWED_ITEM_ID)) {
    const geometry = read.geometry(row);
    const size = geometry?.rulesDiameter ?? geometry?.diameter;
    if (!size || !(size > 0)) throw new Error('movement:burrow-body-unavailable');
    if (!bodyInSolid(solidContextFromEncounter(encounter, read.statuses), { ...point, seg: 0 }, size / 2, size)) duties.push('Burrowed-reconcile');
  }
  return duties;
}

/** Decide the retired solid receiver's unserved dispatch duties before saving the move.
 * The same command, including its GM log reasons, survives transport retries.
 */
export function prepareMovementDispatchSkips(command: MovementTruthCommand, context?: MovementDispatchContext): void {
  if (command.dispatch_skips !== undefined) return;
  const reasons: string[] = [];
  const actor = context?.follower(command.actor_id);
  if (actor && context?.read.hasStatus(actor, 'hunkered_down'))
    command.rest = { ...command.rest, hunkered: true, squeeze: command.rest?.squeeze ?? null };
  if (command.teleport && command.teleport.dice > 0) {
    const actor = context?.follower(command.actor_id);
    if (!context || !actor) throw new Error('movement:actor-state-unavailable');
    reasons.push(`movement:teleport-displacement-damage-unported:${escapeHtml(context.name(actor))}:${command.teleport.displaced_ft}ft:${command.teleport.dice}x2d10`);
  }
  const squeezes = new Map<string, SqueezeState>();
  const hunkers: string[] = [];
  for (const member of command.compound?.members ?? []) {
    if (!member.id) continue; // Slaved inventory has no creature settlement duties.
    const row = context?.follower(member.id);
    if (!context || !row) throw new Error('movement:follower-state-unavailable');
    const squeeze = squeezeStateAt(context.encounter, row, context.read, member.after.x_pos, member.after.y_pos, member.after.z_pos);
    if (!squeeze) throw new Error('movement:squeeze-state-unavailable');
    if (squeeze.squeezed !== context.read.hasStatus(row, 'squeezed') || squeeze.very !== context.read.hasStatus(row, 'very_squeezed')) squeezes.set(member.id, squeeze);
    if (context.read.hasStatus(row, 'hunkered_down')) hunkers.push(member.id);
    const duties = settlementDuties(row, { x: member.after.x_pos, y: member.after.y_pos, z: member.after.z_pos }, context);
    if (duties.length) reasons.push(`movement:follower-settlement-unported:${escapeHtml(context.name(row))}:${duties.join(',')}`);
  }
  // Each follower whose Squeezed / Very Squeezed state CHANGES at its stop travels WITH the operation: the outbox fingerprints and
  // freezes this command right after prepare(), so every transport retry re-reads the same decision from its own bytes, however many
  // operations follow (Astra r3 critic: an evictable side map lost it after 512). The posted creature member still carries exactly the
  // receiver's columns (id, expected_rev, before, after) — post() omits this planning-only field; the decision reaches the follower's
  // rows through the same native `status_plan` as the mover's (movementStatusInputs20 below).
  if (squeezes.size) command.follower_squeezes = Object.fromEntries(squeezes);
  if (hunkers.length) command.follower_hunkers = hunkers;
  command.dispatch_skips = reasons.map(reason => skipUnported(reason,
    { encounterId: command.encounter_id, actorId: command.actor_id }).rolls_full);
}

/** The movement status planner's inputs for a prepared command: the posted bytes plus each follower's end-of-move squeeze
 *  decision, read from the command itself. The command is returned unchanged when no follower's state changes. */
export function movementStatusInputs20(command: MovementTruthCommand): MovementStatusInputs20 {
  const squeezes: Partial<Record<string, SqueezeState>> | undefined = command.follower_squeezes;
  const hunkers = new Set(command.follower_hunkers);
  if ((!squeezes && !hunkers.size) || !command.compound) return command;
  return { ...command, compound: { members: command.compound.members.map(member => {
    const squeeze = member.id ? squeezes?.[member.id] : undefined;
    const hunkered = !!member.id && hunkers.has(member.id);
    return squeeze || hunkered ? { ...member, ...(squeeze ? { squeeze } : {}), ...(hunkered ? { hunkered: true } : {}) } : member;
  }) } };
}
